/**
 * Vessel Context
 * 
 * Manages the currently selected vessel across the application
 * Loads vessels from Dexie or Supabase based on data source
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useDataSource } from './DataSourceContext';
import { useAuth } from './AuthContext';
import { db } from '../lib/database/db';
import { createLogger } from '../lib/utils/logger';

const logger = createLogger('VesselContext');

interface Vessel {
  id: string;
  name: string;
  imo: string;
  flag: string;
  type: string;
  timezone?: string;  // IANA timezone (e.g., 'Europe/Athens', 'Asia/Singapore'). Default: 'UTC'
}

interface VesselContextType {
  currentVessel: Vessel | null;
  setCurrentVessel: (vessel: Vessel) => void;
  vessels: Vessel[];
  loading: boolean;
}

const VesselContext = createContext<VesselContextType | undefined>(undefined);

export function VesselProvider({ children }: { children: ReactNode }) {
  const { dataSource } = useDataSource();
  const { currentUser } = useAuth();
  const [currentVessel, setCurrentVessel] = useState<Vessel | null>(null);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [loading, setLoading] = useState(true);

  const loadVessels = useCallback(async () => {
    setLoading(true);
    try {
      if (dataSource === 'supabase' && currentUser?.organizationId) {
        logger.info('Loading vessels from Supabase...');
        const { fetchData } = await import('@/lib/services/dataSourceService');
        const { data, error } = await fetchData<any>('ships', 'supabase', {
          organizationId: currentUser.organizationId,
          filters: { status: 'active' },
          orderBy: 'name',
        });

        if (error) {
          logger.error('Error loading vessels from Supabase:', error);
          setVessels([]);
        } else {
          const mappedVessels: Vessel[] = (data || []).map((ship: any) => ({
            id: ship.id,
            name: ship.name,
            imo: ship.imo_number || '',
            flag: ship.flag_state || '',
            type: ship.vessel_type || '',
          }));

          setVessels(mappedVessels);

          setCurrentVessel(prev => {
            if (!prev && mappedVessels.length > 0) {
              return mappedVessels[0];
            }
            return prev;
          });

          logger.info('Loaded vessels from Supabase', { count: mappedVessels.length });
        }
      } else {
        logger.info('Loading vessels from Dexie...');
        
        // First, audit the database state
        const auditResult = await db.auditDatabase();
        
        // If no data, initialize sample data
        if (!auditResult.hasData) {
          logger.info('No data found in Dexie. Initializing sample data...');
          await db.initializeSampleData();
          await db.auditDatabase();
        }
        
        const ships = await db.ships
          .where('status')
          .equals('active')
          .toArray();
        
        logger.info('Found active ships in Dexie', { count: ships.length });
        
        const mappedVessels: Vessel[] = ships.map(ship => ({
          id: ship.id,
          name: ship.name,
          imo: ship.imoNumber || '',
          flag: ship.flag || '',
          type: ship.vesselType || '',
        }));
        
        setVessels(mappedVessels);
        
        // Set first vessel as current if none selected
        // Only set if currentVessel is actually null (not in dependency array to avoid loop)
        setCurrentVessel(prev => {
          if (!prev && mappedVessels.length > 0) {
            return mappedVessels[0];
          }
          return prev;
        });
        
        logger.info('Loaded vessels from Dexie', { count: mappedVessels.length });
      }
    } catch (error) {
      logger.error('Error loading vessels:', error);
      setVessels([]);
    } finally {
      setLoading(false);
    }
  }, [dataSource, currentUser]);

  useEffect(() => {
    loadVessels();
  }, [loadVessels]);

  // Log vessel changes for debugging
  useEffect(() => {
    if (currentVessel) {
      logger.info('Current vessel changed', { vessel: currentVessel.name });
    }
  }, [currentVessel]);

  return (
    <VesselContext.Provider value={{ currentVessel, setCurrentVessel, vessels, loading }}>
      {children}
    </VesselContext.Provider>
  );
}

export function useVessel() {
  const context = useContext(VesselContext);
  if (context === undefined) {
    throw new Error('useVessel must be used within a VesselProvider');
  }
  return context;
}


